import { TicketStatus, parseTicketStatus } from './tickets.types';

export interface TicketStatusMappingResult {
  status: TicketStatus | null;
  known: boolean;
}

const DEFAULT_STATUS_MAP: Partial<Record<string, TicketStatus>> = {
  'to do': TicketStatus.NEW,
  todo: TicketStatus.NEW,
  new: TicketStatus.NEW,
  backlog: TicketStatus.NEW,
  pending: TicketStatus.NEW,
  open: TicketStatus.NEW,
  inbox: TicketStatus.NEW,
  incoming: TicketStatus.NEW,
  'in progress': TicketStatus.IN_PROGRESS,
  'in-progress': TicketStatus.IN_PROGRESS,
  doing: TicketStatus.IN_PROGRESS,
  working: TicketStatus.IN_PROGRESS,
  active: TicketStatus.IN_PROGRESS,
  review: TicketStatus.IN_PROGRESS,
  'in review': TicketStatus.IN_PROGRESS,
  reviewing: TicketStatus.IN_PROGRESS,
  done: TicketStatus.RESOLVED,
  resolved: TicketStatus.RESOLVED,
  complete: TicketStatus.RESOLVED,
  completed: TicketStatus.RESOLVED,
  fixed: TicketStatus.RESOLVED,
  closed: TicketStatus.CLOSED,
  archived: TicketStatus.CLOSED,
  cancelled: TicketStatus.CANCELLED,
  canceled: TicketStatus.CANCELLED,
};

const JIRA_STATUS_MAP: Partial<Record<string, TicketStatus>> = {
  'selected for development': TicketStatus.NEW,
  'in development': TicketStatus.IN_PROGRESS,
  'code review': TicketStatus.IN_PROGRESS,
  testing: TicketStatus.IN_PROGRESS,
  "won't do": TicketStatus.CANCELLED,
  "won't fix": TicketStatus.CANCELLED,
};

const CLICKUP_STATUS_MAP: Partial<Record<string, TicketStatus>> = {
  testing: TicketStatus.IN_PROGRESS,
};

export function mapTicketStatusName(
  rawStatus: string,
  customMap: Partial<Record<string, TicketStatus>> = {},
): TicketStatusMappingResult {
  const normalizedStatus = rawStatus.toLowerCase().trim();
  const status = customMap[normalizedStatus] ?? DEFAULT_STATUS_MAP[normalizedStatus];

  return {
    status: status ?? null,
    known: status !== undefined,
  };
}

function mapJiraStatusCategory(
  statusCategory: string | undefined,
  resolution: string | null | undefined,
): TicketStatus | undefined {
  switch (statusCategory) {
    case 'new':
      return TicketStatus.NEW;
    case 'indeterminate':
      return TicketStatus.IN_PROGRESS;
    case 'done':
      return mapJiraDoneStatus(resolution);
    default:
      return undefined;
  }
}

function mapJiraDoneStatus(
  resolution: string | null | undefined,
): TicketStatus {
  if (resolution === undefined || resolution === null || resolution === '') {
    return TicketStatus.RESOLVED;
  }

  const normalizedResolution = resolution.toLowerCase();
  if (
    normalizedResolution.includes("won't") ||
    normalizedResolution.includes('duplicate') ||
    normalizedResolution.includes('invalid') ||
    normalizedResolution.includes('cannot')
  ) {
    return TicketStatus.CLOSED;
  }

  return TicketStatus.RESOLVED;
}

export function mapJiraTicketStatus(
  rawStatus: string,
  statusCategory?: string,
  resolution?: string | null,
): TicketStatusMappingResult {
  const mappedCategory = mapJiraStatusCategory(statusCategory, resolution);
  if (mappedCategory !== undefined) {
    return { status: mappedCategory, known: true };
  }

  return mapTicketStatusName(rawStatus, JIRA_STATUS_MAP);
}

export function mapClickUpTicketStatus(
  rawStatus: string,
  statusType?: string,
  archived?: boolean,
): TicketStatusMappingResult {
  if (archived === true) {
    return { status: TicketStatus.CLOSED, known: true };
  }

  if (statusType !== undefined && statusType !== '') {
    switch (statusType) {
      case 'open':
        return { status: TicketStatus.NEW, known: true };
      case 'done':
        return { status: TicketStatus.RESOLVED, known: true };
      case 'closed':
        return { status: TicketStatus.CLOSED, known: true };
      case 'custom':
        break;
    }
  }

  return mapTicketStatusName(rawStatus, CLICKUP_STATUS_MAP);
}

export function normalizeTicketStatusMap(
  rawMap: Record<string, string>,
): Record<string, TicketStatus> {
  return Object.entries(rawMap).reduce<Record<string, TicketStatus>>(
    (normalized, [rawName, rawStatus]) => {
      const status = parseTicketStatus(rawStatus);
      if (status !== undefined) {
        normalized[rawName.toLowerCase().trim()] = status;
      }
      return normalized;
    },
    {},
  );
}
